import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X as XIcon, Loader2, FileText, Image as ImgIcon, File as FileIcon, Download, MapPin, ChevronDown } from "lucide-react";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import api from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

function tipoIcono(adj) {
  const tipo = adj.tipo || "";
  if (tipo.startsWith("image/")) return ImgIcon;
  if (tipo === "application/pdf") return FileText;
  return FileIcon;
}

function paradaLabel(paradas, linea_id) {
  if (!linea_id) return "Global · toda la ruta";
  const p = (paradas || []).find((x) => x.linea_id === linea_id);
  if (!p) return "Parada eliminada";
  const nom = p.espacio?.nombre || p.espacio_nombre || "—";
  return `#${p.orden} · ${nom}`;
}

export default function RutaAdjuntos({
  rutaId,
  adjuntos = [],
  onChange,
  canEdit = true,
  label = "Documentos para los montadores",
  paradas = [],
}) {
  const [uploading, setUploading] = useState(false);
  const [target, setTarget] = useState(""); // "" = global, o linea_id
  const [filter, setFilter] = useState("all"); // "all" | "global" | linea_id

  const grouped = useMemo(() => {
    const g = { global: [], byLinea: {} };
    for (const a of adjuntos) {
      if (a.linea_id) {
        g.byLinea[a.linea_id] = g.byLinea[a.linea_id] || [];
        g.byLinea[a.linea_id].push(a);
      } else {
        g.global.push(a);
      }
    }
    return g;
  }, [adjuntos]);

  const visibles = useMemo(() => {
    if (filter === "all") return adjuntos;
    if (filter === "global") return grouped.global;
    return grouped.byLinea[filter] || [];
  }, [adjuntos, filter, grouped]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let added = 0;
    const linea_id = target || null;
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: demasiado grande (máx 50MB)`);
        continue;
      }
      try {
        const up = await uploadToCloudinary(file, "dnf/rutas", "auto");
        const { data } = await api.post(`/rutas/${rutaId}/adjuntos`, {
          nombre: file.name,
          url: up.secure_url,
          tipo: file.type || null,
          tamano: file.size,
          public_id: up.public_id,
          linea_id,
        });
        onChange && onChange((prev) => [...(prev || []), data]);
        added++;
      } catch (err) {
        toast.error(err.message || `Error subiendo ${file.name}`);
      }
    }
    if (added > 0) {
      toast.success(
        target
          ? `${added} archivo(s) subido(s) a ${paradaLabel(paradas, target)}`
          : `${added} archivo(s) subido(s) (global)`
      );
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeAdj = async (adj) => {
    if (!window.confirm(`¿Eliminar ${adj.nombre}?`)) return;
    try {
      await api.delete(`/rutas/${rutaId}/adjuntos/${adj.id}`);
      onChange && onChange((prev) => (prev || []).filter((a) => a.id !== adj.id));
      toast.success("Archivo eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const moverAdj = async (adj, nuevoLineaId) => {
    try {
      const lid = nuevoLineaId || null;
      const { data } = await api.patch(`/rutas/${rutaId}/adjuntos/${adj.id}`, { linea_id: lid });
      onChange && onChange((prev) => (prev || []).map((a) => (a.id === adj.id ? { ...a, linea_id: data.linea_id } : a)));
      toast.success(`Movido a ${paradaLabel(paradas, lid)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al mover");
    }
  };

  const hasParadas = (paradas || []).length > 0;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm font-semibold flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-amber-400" /> {label}
          <span className="text-xs text-zinc-500 font-normal">({adjuntos.length})</span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            {hasParadas && (
              <div className="relative">
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="ruta-adjunto-target-select"
                  className="bg-zinc-950 border border-zinc-800 text-zinc-200 rounded pl-2 pr-7 py-1 text-xs appearance-none">
                  <option value="">📎 Global (toda la ruta)</option>
                  {paradas.map((p) => (
                    <option key={p.linea_id} value={p.linea_id}>
                      📍 #{p.orden} · {p.espacio?.nombre || p.espacio_nombre || "—"}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            )}
            <label className="cursor-pointer">
              <input type="file" multiple onChange={onFiles} className="hidden"
                accept="image/*,application/pdf,.psd,.ai,.zip,.dxf"
                data-testid="ruta-adjunto-input" />
              <span className={`inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 h-7 px-2 text-xs rounded ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Añadir archivos
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Filtros (sólo si hay paradas y al menos algún adjunto por parada) */}
      {hasParadas && adjuntos.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="adjuntos-filter">
          <button
            onClick={() => setFilter("all")}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
              filter === "all" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-amber-300"
            }`}>
            Todos ({adjuntos.length})
          </button>
          {grouped.global.length > 0 && (
            <button
              onClick={() => setFilter("global")}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                filter === "global" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-amber-300"
              }`}>
              📎 Global ({grouped.global.length})
            </button>
          )}
          {paradas.map((p) => {
            const n = (grouped.byLinea[p.linea_id] || []).length;
            if (n === 0) return null;
            return (
              <button
                key={p.linea_id}
                onClick={() => setFilter(p.linea_id)}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                  filter === p.linea_id ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-amber-300"
                }`}>
                #{p.orden} {p.espacio?.nombre || "—"} ({n})
              </button>
            );
          })}
        </div>
      )}

      {adjuntos.length === 0 && (
        <div className="text-xs text-zinc-500 italic text-center py-4">
          Sin archivos. {canEdit ? "Sube renders, instrucciones o fotos de referencia y asígnalos a una parada concreta o a toda la ruta." : "El admin no ha subido documentos para esta ruta."}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {visibles.map((adj) => {
          const Ico = tipoIcono(adj);
          const isImg = (adj.tipo || "").startsWith("image/");
          const tagLabel = paradaLabel(paradas, adj.linea_id);
          const tagColor = adj.linea_id
            ? "bg-cyan-500/20 text-cyan-200 border-cyan-500/30"
            : "bg-amber-500/20 text-amber-200 border-amber-500/30";
          return (
            <div key={adj.id} data-testid={`adjunto-${adj.id}`}
              className="relative bg-zinc-950/60 border border-zinc-800 rounded-lg overflow-hidden group">
              {isImg ? (
                <a href={adj.url} target="_blank" rel="noreferrer" className="block">
                  <img src={cloudinaryThumb(adj.url, 240, 160)} alt={adj.nombre}
                    className="w-full h-24 object-cover" />
                </a>
              ) : (
                <a href={adj.url} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center h-24 bg-zinc-900 text-zinc-500 hover:text-amber-300">
                  <Ico className="w-10 h-10" />
                </a>
              )}
              <div className="p-1.5">
                <div className="text-[11px] text-zinc-200 truncate font-medium" title={adj.nombre}>{adj.nombre}</div>
                <div className={`inline-flex items-center gap-0.5 text-[9px] mt-0.5 px-1.5 py-0.5 rounded border ${tagColor}`}
                  title={tagLabel}
                  data-testid={`adjunto-${adj.id}-tag`}>
                  <MapPin className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[110px]">{tagLabel}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <a href={adj.url} target="_blank" rel="noreferrer"
                    className="text-[9px] text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5">
                    <Download className="w-2.5 h-2.5" /> Abrir
                  </a>
                  {canEdit && hasParadas && (
                    <select
                      value={adj.linea_id || ""}
                      onChange={(e) => moverAdj(adj, e.target.value)}
                      data-testid={`adjunto-${adj.id}-move`}
                      className="text-[9px] bg-zinc-900 border border-zinc-800 rounded px-1 py-0.5 text-zinc-300 max-w-[90px]">
                      <option value="">→ Global</option>
                      {paradas.map((p) => (
                        <option key={p.linea_id} value={p.linea_id}>
                          → #{p.orden} {p.espacio?.nombre || "—"}
                        </option>
                      ))}
                    </select>
                  )}
                  {canEdit && (
                    <button onClick={() => removeAdj(adj)}
                      className="text-zinc-500 hover:text-red-400">
                      <XIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
